// Re-export crop utilities for convenience
export type { StandardCropData, LegacyCropData, AnyCropData } from './cropUtils';

// Image categories with their aspect ratios
export type UIImageCategory = 'ProjectPortrait' | 'ProjectLandscape' | 'SceneLandscape' | 'TemplateLandscape' | 'ScanImage' | 'ImageAnchor' | 'Avatar' | 'SceneVideo';

// A TemplateLandscape row at this order is the template's own cover; orders 1 and up
// are the extra gallery images. Templates published before the template cover existed
// have no such row, so every reader falls back to the story's SceneLandscape. Match it
// strictly (=== 0): a null order means "unordered extra", not "cover".
export const TEMPLATE_COVER_ORDER = 0;

export interface UIImageCategoryConfig {
  aspectRatio: '9:16' | '16:9' | '1:1';
  description: string;
  isVideo?: boolean; // Indicates if this category is for video files
  maxSizeMB?: number; // Max file size in MB (defaults to 20 for images, higher for videos)
}

export const UI_IMAGE_CATEGORIES: Record<UIImageCategory, UIImageCategoryConfig> = {
  ProjectPortrait: {
    aspectRatio: '9:16',
    description: 'Portrait cover image for projects',
    maxSizeMB: 20
  },
  ProjectLandscape: {
    aspectRatio: '16:9',
    description: 'Landscape cover image for projects',
    maxSizeMB: 20
  },
  SceneLandscape: {
    aspectRatio: '16:9',
    description: 'Landscape image for scenes',
    maxSizeMB: 20
  },
  TemplateLandscape: {
    aspectRatio: '16:9',
    description: 'Additional landscape images for template scenes',
    maxSizeMB: 20
  },
  ScanImage: {
    aspectRatio: '1:1',
    description: 'Square image for scans',
    maxSizeMB: 20
  },
  ImageAnchor: {
    aspectRatio: '1:1',
    description: 'Square image for image anchors',
    maxSizeMB: 20
  },
  Avatar: {
    aspectRatio: '1:1',
    description: 'User profile avatar image',
    maxSizeMB: 20
  },
  SceneVideo: {
    aspectRatio: '16:9',
    description: 'Video for template scenes',
    isVideo: true,
    maxSizeMB: 500 // 500MB max for videos
  }
};

export interface UIImage {
  id: number;
  createdAt: string;
  imageId: string;
  projectInstanceId?: string;
  sceneOriginId?: string;
  sceneInstanceId?: string;
  imageCategory?: UIImageCategory;
  imageMetadata?: Record<string, any>;
  imageOriginalUrl?: string;
  imageCroppedUrl?: string;
  userId?: string;
  imageOrderNumber?: number;
  assetTypeId?: string;
}

export interface SupabaseUIImage {
  id: number;
  created_at: string;
  image_id: string;
  project_instance_id?: string;
  scene_origin_id?: string;
  scene_instance_id?: string;
  image_category?: string;
  image_metadata?: Record<string, any>;
  image_original_url?: string;
  image_cropped_url?: string;
  user_id?: string;
  image_order_number?: number;
  asset_type_id?: string;
}

export interface CreateUIImageParams {
  projectInstanceId?: string;
  sceneOriginId?: string;
  sceneInstanceId?: string;
  imageCategory?: UIImageCategory;
  imageMetadata?: Record<string, any>;
  imageOriginalUrl?: string;
  imageCroppedUrl?: string;
  userId?: string;
  imageOrderNumber?: number;
  assetTypeId?: string;
}

export interface UpdateUIImageParams {
  imageCategory?: UIImageCategory;
  imageMetadata?: Record<string, any>;
  imageOriginalUrl?: string;
  imageCroppedUrl?: string;
  imageOrderNumber?: number;
  assetTypeId?: string;
}

export interface UIImageQueryParams {
  projectInstanceId?: string;
  sceneOriginId?: string;
  sceneInstanceId?: string;
  imageCategory?: UIImageCategory;
  /** Several categories in one read; with imageCategory, both count. */
  imageCategories?: UIImageCategory[];
  userId?: string;
}

export interface UIImageResult {
  success: boolean;
  data?: UIImage;
  error?: string;
}

export interface UIImageListResult {
  success: boolean;
  data?: UIImage[];
  error?: string;
}

// Provider interfaces for dependency injection
export interface StorageProvider {
  /** Answers where the file was stored: keep that url, never one built from the path asked for. */
  uploadFile(path: string, file: File | Blob, options?: any): Promise<{ data?: { path: string; url: string } | null; error?: Error }>;
  getPublicUrl(path: string): { data: { publicUrl: string } };
}

// Utility functions for aspect ratio validation
export const validateImageAspectRatio = (
  file: File, 
  requiredCategory: UIImageCategory, 
  tolerance: number = 0.15
): Promise<{ isValid: boolean; actualRatio: number; expectedRatio: number; error?: string }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const actualRatio = img.width / img.height;
      const categoryConfig = UI_IMAGE_CATEGORIES[requiredCategory];
      const expectedRatio = categoryConfig.aspectRatio === '9:16' ? 9/16 : 
                           categoryConfig.aspectRatio === '16:9' ? 16/9 : 1;
      
      const isValid = Math.abs(actualRatio - expectedRatio) < tolerance;
      
      resolve({
        isValid,
        actualRatio,
        expectedRatio,
        error: isValid ? undefined : `Image aspect ratio ${actualRatio.toFixed(2)} does not match required ${categoryConfig.aspectRatio}`
      });
    };
    img.onerror = () => {
      resolve({
        isValid: false,
        actualRatio: 0,
        expectedRatio: 0,
        error: 'Failed to load image for validation'
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

export const getAspectRatioFromCategory = (category: UIImageCategory): string => {
  return UI_IMAGE_CATEGORIES[category].aspectRatio;
};

export const getAvailableCategories = (): UIImageCategory[] => {
  return Object.keys(UI_IMAGE_CATEGORIES) as UIImageCategory[];
};

export const getCategoryConfig = (category: UIImageCategory): UIImageCategoryConfig => {
  return UI_IMAGE_CATEGORIES[category];
};
